import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const paymentMethodKeys = {
  all: ['paymentMethods'],
  lists: () => [...paymentMethodKeys.all, 'list'],
  details: () => [...paymentMethodKeys.all, 'detail'],
  detail: (id) => [...paymentMethodKeys.details(), id],
};

export const usePaymentMethods = () => {
  return useQuery({
    queryKey: paymentMethodKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('payment_methods');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const usePaymentMethod = (id, options = {}) => {
  return useQuery({
    queryKey: paymentMethodKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`payment_methods/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

export const useCreatePaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentMethodData) => {
      const response = await apiClient.post('payment_methods', paymentMethodData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentMethodKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating payment method:', error);
    }
  });
};

export const useUpdatePaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`payment_methods/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(paymentMethodKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: paymentMethodKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating payment method:', error);
    }
  });
};

export const useDeletePaymentMethod = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`payment_methods/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: paymentMethodKeys.lists() });
      queryClient.removeQueries({ queryKey: paymentMethodKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting payment method:', error);
    }
  });
};